import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { IStoreState } from "../initialStoreState";
import {
  convertLoaderCompleted,
  convertLoaderProgress,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IOpportunities } from "./opportunites.types";
import { fetchLeadsListProgress } from "../leads/leadsActions";

export const FETCH_OPPORTUNITES_LIST_PROGRESS =
  "FETCH_OPPORTUNITES_LIST_PROGRESS";
export const FETCH_OPPORTUNITES_LIST_SUCCESS =
  "FETCH_OPPORTUNITES_LIST_SUCCESS";
export const FETCH_OPPORTUNITES_LIST_FAILED = "FETCH_OPPORTUNITES_LIST_FAILED";

export const fetchOpportunitesListProgress = () =>
  action(FETCH_OPPORTUNITES_LIST_PROGRESS);
export const fetchOpportunitesListSuccess = (
  data: IOpportunities[],
  totalRecords: number,
  currentRecords: number,
  meta: { use_count: number; created_count: number }
) =>
  action(FETCH_OPPORTUNITES_LIST_SUCCESS, {
    data,
    totalRecords,
    currentRecords,
    meta,
  });
export const fetchOpportunitesListFailed = () =>
  action(FETCH_OPPORTUNITES_LIST_FAILED);

export const fetchOpportunitesListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    venue_uuid?: string,
    searchByVenue?: string,
    company_outlet_uuid?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchOpportunitesListProgress());

      let url = "";

      if (status !== "-1") {
        url += `&status=${status}`;
      }
      if (venue_uuid) {
        url += `&venue_uuid=${venue_uuid}`;
      }
      if (company_outlet_uuid) {
        url += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      if (searchValue.length > 0 || searchByVenue) {
        const advanceFilter = [...searchValue];

        if (searchByVenue) {
          advanceFilter.push({
            column: ["venue_name"],
            value: searchByVenue,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        url += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      const finalUrl =
        `/venue/get-venue?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${rowsInPerPage}&from_date=${date.fromDate}&to_date=${date.toDate}` +
        url;

      // Make the API call
      const response = await api.get(finalUrl);

      // Check if the response is valid
      if (response.status === 200 && response.data) {
        const data: IOpportunities[] = response.data.data;

        dispatch(
          fetchOpportunitesListSuccess(
            data,
            response.data.totalRecords,
            response.data.currentRecords,
            response.data.meta
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchOpportunitesListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_OPPORTUNITES_PROGRESS = "FETCH_OPPORTUNITES_PROGRESS";
export const FETCH_OPPORTUNITES_SUCCESS = "FETCH_OPPORTUNITES_SUCCESS";
export const FETCH_OPPORTUNITES_FAILED = "FETCH_OPPORTUNITES_FAILED";

export const fetchOpportunitesProgress = () =>
  action(FETCH_OPPORTUNITES_PROGRESS);
export const fetchOpportunitesSuccess = (data: IOpportunities) =>
  action(FETCH_OPPORTUNITES_SUCCESS, { data });
export const fetchOpportunitesFailed = (errorMessage: string) =>
  action(FETCH_OPPORTUNITES_FAILED, { errorMessage });

export const fetchOpportunitesAysnc =
  (oppId: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchOpportunitesProgress());

      let finalUrl = `/venue/get-venue?pageNo=1&itemPerPage=10&venue_uuid=${oppId}`;

      const res = await api.get(finalUrl);
      const data: IOpportunities[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchOpportunitesSuccess(data[0]));
      } else {
        dispatch(
          fetchOpportunitesFailed(
            "Oops! We couldn't find any records associated with your opportunites number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchOpportunitesFailed(""));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const upsertOpportunitesAysnc =
  (
    existingLead: IOpportunities,
    onCallBack: (isSuccess?: boolean, data?: IOpportunities) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(saveLoaderProgress());
    try {
      const { ...rest } = existingLead;

      const res = await api.post("/venue/upsert-venue", rest);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );

      const data = res.data.data;
      onCallBack(true, data);
    } catch (err: any) {
      onCallBack(false);

      // Handle different error response formats
      let errorMessage = "An error occurred";
      if (err.response && err.response.data) {
        // For subscription limit errors (403 message in 401 response)
        if (err.response.data.message === 403 && err.response.data.data) {
          errorMessage = err.response.data.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }

      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const convertToStudentAsync =
  (
    opportunityUUID: string,
    onCallBack: (isSuccess?: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    dispatch(convertLoaderProgress());
    try {
      const res = await api.post("/leads/convert-opportunity-into-student", {
        opportunity_uuid: opportunityUUID,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Successfully converted to Student!",
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallBack(true, data);
    } catch (err: any) {
      onCallBack(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(convertLoaderCompleted());
    }
  };

export const fetchVenueHoldAvailabilityAsync =
  (
    venueId: string,
    from_date: string,
    to_date: string,

    onCallback?: (isSuccess: boolean, data?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchLeadsListProgress());
      let url = `/occupancy/check-availability?venue_uuid=${venueId}&from_date=${from_date}&to_date=${to_date}`;

      const res = await api.get(url);

      const data = res.data.data;

      onCallback?.(true, data);
    } catch (err: any) {
      onCallback?.(false, null);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_OPPORTUNITES_STATE = "CLEAR_OPPORTUNITES_STATE";
export const CLEAR_OPPORTUNITES = "CLEAR_OPPORTUNITES";

export const clearOpportunityState = () => action(CLEAR_OPPORTUNITES_STATE);
export const clearOpportunity = () => action(CLEAR_OPPORTUNITES);
